/**
 * Conversion Funnel Chart Component
 * Displays lead conversion funnel with drop-off rates
 * Phase 6.2 - Performance Charts
 */

import React from 'react';
import { Box, Typography, Skeleton, LinearProgress } from '@mui/material';
import { TrendingDown } from '@mui/icons-material';

export interface FunnelStage {
  stage: string;
  count: number;
  percentage: number;
}

export interface ConversionFunnelChartProps {
  data: FunnelStage[];
  loading?: boolean;
}

const ConversionFunnelChart: React.FC<ConversionFunnelChartProps> = ({
  data,
  loading = false
}) => {
  // Calculate drop-off rates between stages
  const getDropOffRate = (currentIndex: number): number => {
    if (currentIndex === 0) return 0;
    const currentPercentage = data[currentIndex].percentage;
    const previousPercentage = data[currentIndex - 1].percentage;
    return previousPercentage - currentPercentage;
  };

  // Color gradient based on stage
  const getStageColor = (index: number, total: number): string => {
    const ratio = 1 - (index / (total - 1));
    // Gradient from green (top) to orange/red (bottom)
    if (ratio > 0.7) return '#2e7d32'; // Dark green
    if (ratio > 0.5) return '#66bb6a'; // Light green
    if (ratio > 0.3) return '#ffa726'; // Orange
    return '#ef5350'; // Red
  };

  // Background color (lighter version)
  const getStageBgColor = (index: number, total: number): string => {
    const ratio = 1 - (index / (total - 1));
    if (ratio > 0.7) return 'rgba(46, 125, 50, 0.1)';
    if (ratio > 0.5) return 'rgba(102, 187, 106, 0.1)';
    if (ratio > 0.3) return 'rgba(255, 167, 38, 0.1)';
    return 'rgba(239, 83, 80, 0.1)';
  };

  return (
    <Box>
      {/* Header */}
      <Typography variant="h6" fontWeight="600" mb={3}>
        Conversion Funnel Analysis
      </Typography>

      {loading ? (
        <Skeleton variant="rectangular" height={500} sx={{ borderRadius: 1 }} />
      ) : (
        <Box>
          {data.map((stage, index) => {
            const dropOffRate = getDropOffRate(index);
            const color = getStageColor(index, data.length);
            const bgColor = getStageBgColor(index, data.length);

            // Calculate width as percentage of first stage
            const widthPercentage = stage.percentage;

            return (
              <Box key={stage.stage} mb={index < data.length - 1 ? 2 : 0}>
                {/* Stage Bar */}
                <Box
                  sx={{
                    width: `${widthPercentage}%`,
                    minWidth: '30%',
                    backgroundColor: bgColor,
                    border: `2px solid ${color}`,
                    borderRadius: 1,
                    p: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(8px)',
                      boxShadow: 2
                    }
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="body1" fontWeight="600" color={color}>
                      {stage.stage}
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="h6" fontWeight="700" color={color}>
                        {stage.count}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ({stage.percentage.toFixed(1)}%)
                      </Typography>
                    </Box>
                  </Box>

                  {/* Progress bar showing percentage of total */}
                  <LinearProgress
                    variant="determinate"
                    value={stage.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: color,
                        borderRadius: 4
                      }
                    }}
                  />
                </Box>

                {/* Drop-off indicator */}
                {index < data.length - 1 && dropOffRate > 0 && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={1}
                    ml={2}
                    my={1}
                    sx={{ opacity: 0.7 }}
                  >
                    <TrendingDown sx={{ fontSize: 16, color: '#d32f2f' }} />
                    <Typography variant="caption" color="error">
                      Drop-off: {dropOffRate.toFixed(1)}% ({data[index].count - data[index + 1].count} leads)
                    </Typography>
                  </Box>
                )}
              </Box>
            );
          })}

          {/* Summary */}
          <Box
            mt={4}
            p={2}
            bgcolor="rgba(25, 118, 210, 0.08)"
            borderRadius={1}
            border="1px solid rgba(25, 118, 210, 0.2)"
          >
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" fontWeight="600" color="primary">
                Overall Conversion Rate
              </Typography>
              <Typography variant="h6" fontWeight="700" color="primary">
                {data.length > 0 ? data[data.length - 1].percentage.toFixed(1) : 0}%
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {data.length > 0 && data[0].count > 0
                ? `${data[data.length - 1].count} out of ${data[0].count} leads converted`
                : 'No conversion data available'}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ConversionFunnelChart;
